<template>
  <v-dialog
    v-model="isOpenModal"
    :scrim="false"
    fullscreen
    transition="slide-x-transition"
    @click:outside="onClose"
  >
    <v-card class="left-vertical-card">
      <div class="d-flex bg-titre flex-column pl-3 pt-2">
        <div class="d-flex align-center">
          <div
            v-if="!isTresoMoissonAFixer"
            :class="isTresoMoisson ? '' : 'mr-auto'"
            class="secondary--text align-self-center titre-h3"
          >
            {{ titreAffiche | capitalize }}
          </div>
          <TresoMoissonChip
            v-if="isTresoMoisson"
            :is-a-fixer="isTresoMoissonAFixer"
            class="ml-3 mr-auto"
          />

          <v-btn
            class="align-self-center"
            text
            @click="onClose"
          >
            <v-icon color="secondary">
              mdi-close
            </v-icon>
          </v-btn>
        </div>
        <div>
          <span
            v-if="!isContratNonEngage && !isTresoMoissonAFixer"
            class="texte-gris-fonce texte"
          >
            Souscrit le {{ resumeContrat.dateSouscription | date('dd/MM/yyyy') }} - N°{{ resumeContrat.numeroContrat }}
          </span>
        </div>
      </div>
      <BaseLoader v-if="loading.detailsContrat" />
      <div
        v-else
        class="d-flex flex-column mx-4"
        :class="$vuetify.breakpoint.mobile ? 'over' : ''"
      >
        <div
          v-if="isQuantiteLivreeAffichee"
          class="d-flex pt-2 justify-space-between"
        >
          <span class="intro texte-gris-fonce py-3">
            Poids livré
          </span>
          <span class="titre-h4 py-2 align-end secondary--text">
            {{ detailsContrat.quantiteLivreeEnKg / 1000 | roundNumber(2) }} t
          </span>
        </div>
        <v-divider v-if="isQuantiteLivreeAffichee" />
        <div
          v-if="isQuantitePayeeAffichee"
          class="d-flex pt-2 justify-space-between"
        >
          <span class="intro texte-gris-fonce py-3">
            Poids payé
          </span>
          <span class="titre-h4 py-2 align-end secondary--text">
            {{ detailsContrat.quantitePayeEnKg / 1000 | roundNumber(2) }} t
          </span>
        </div>
        <v-divider v-if="isQuantitePayeeAffichee" />
        <div
          v-if="detailsContrat.prixAcompteTresoMoissonAFixerEnEuroParTonne"
          class="d-flex pt-2 justify-space-between"
        >
          <span class="intro texte-gris-fonce py-3">
            Prix d'avance
          </span>
          <span class="titre-h4 py-2 align-end secondary--text">
            {{ detailsContrat.prixAcompteTresoMoissonAFixerEnEuroParTonne | roundNumber(2) }} €/t
          </span>
        </div>
        <v-divider v-if="detailsContrat.prixAcompteTresoMoissonAFixerEnEuroParTonne" />
        <div
          v-if="detailsContrat.prixPayeEnEuroParTonne"
          class="d-flex pt-2 justify-space-between"
        >
          <span class="intro texte-gris-fonce py-3">
            Prix payé {{ isPrixProvisoire ? 'provisoire' : '' }}
          </span>
          <span class="titre-h4 py-2 align-end secondary--text">
            {{ detailsContrat.detailsConstructionPrix.prixPayeEnEuroParTonne | roundNumber(2) }} €/t
          </span>
        </div>
        <v-divider v-if="detailsContrat.prixPayeEnEuroParTonne" />
        <div
          v-if="detailsContrat.reglements.length"
          class="d-flex flex-column pt-2 mb-3"
        >
          <span class="intro texte-gris-fonce py-2">
            Règlement{{ detailsContrat.reglements.length > 1 ? 's' : '' }}
          </span>
          <div
            v-for="(reglement, index) in detailsContrat.reglements"
            :key="index"
            class="d-flex my-1 justify-space-between"
          >
            <span
              class="texte texte-gris-fonce type-reglement"
            >
              {{ reglement.libelleTypeReglement }}
            </span>
            <span class="texte secondary--text date-reglement ">
              {{ reglement.dateReglement | date('dd/MM/yyyy') }}
            </span>
            <div class="d-flex">
              <v-chip
                :color="getChipColor(reglement.prixReglementEnEuroParTonne)"
                class="d-flex chip justify-center"
                close-icon="mdi-delete"
                label
                small
              >
                <span class="texte --text-white">
                  {{ formatPrixReglement(reglement.prixReglementEnEuroParTonne, 1, '€/t') }}
                </span>
              </v-chip>
              <div v-if="atLeastOneReglementsHaveTooltipIcon">
                <v-tooltip
                  v-if="reglement.quantiteReglementEnKg !== detailsContrat.quantitePayeEnKg"
                  left
                >
                  <template v-slot:activator="{ on }">
                    <v-icon
                      class="icon"
                      v-on="on"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  Valeur sur {{ reglement.quantiteReglementEnKg / 1000 | roundNumber(2) }} t
                </v-tooltip>
                <span
                  v-if="reglement.quantiteReglementEnKg === detailsContrat.quantitePayeEnKg && atLeastOneReglementsHaveTooltipIcon"
                  class="icon px-3"
                >
                  <!--  Simule l'emplacement vide s'il n'y a pas d'icône -->
                </span>
              </div>
            </div>
          </div>
        </div>
        <v-divider v-if="detailsContrat.reglements.length" />
        <div class="d-flex pt-2 justify-space-between">
          <span class="intro texte-gris-fonce py-3">
            Logistique
          </span>
          <span class="titre-h4 py-2 align-end secondary--text">
            {{ detailsContrat.type.logistique | capitalize }}
          </span>
        </div>
        <v-divider v-if="isPrixFerme && detailsContrat.dateDebutPeriodeExecution" />
        <div
          v-if="isPrixFerme && detailsContrat.dateDebutPeriodeExecution"
          class="d-flex align-start pt-2 justify-space-between"
        >
          <span class="intro texte-gris-fonce py-3">
            Période d'exécution
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon
                  class="icon px-3 py-3"
                  v-on="on"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              Période sans frais de dépôt
            </v-tooltip>
          </span>
          <div class="d-flex flex-column align-end">
            <span class="titre-h4 pt-2 secondary--text">
              Du {{ detailsContrat.dateDebutPeriodeExecution | date('dd/MM/yyyy') }}
            </span>
            <span class="titre-h4 secondary--text">
              au {{ detailsContrat.dateFinPeriodeExecution | date('dd/MM/yyyy') }}
            </span>
          </div>
        </div>
        <DetailsContratConstructionPrix
          v-if="detailsContrat.prixPayeEnEuroParTonne"
          :details-contrat="detailsContrat"
          class="py-5"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import BaseLoader from '@/components/common/base-loader.vue'
import DetailsContratConstructionPrix from '@/views/detail-contrats/components/details-contrat-construction-prix.vue'
import {round} from 'lodash'
import {isBefore, parse} from 'date-fns'
import TresoMoissonChip from '@/views/detail-contrats/components/chip/treso-moisson-chip.vue'

export default {
  name: 'DetailsContratModal',
  components: {TresoMoissonChip, DetailsContratConstructionPrix, BaseLoader},
  props: {
    resumeContrat: {
      type: Object,
      required: true
    },
    isOpenModalDetails: {
      type: Boolean,
      default: false
    },
  },

  data () {
    return {
      isOpenModal: false,
      loading: {
        detailsContrat: false,
      },
      erreur: {
        detailsContrat: false
      },
    }
  },

  computed: {
    ...mapGetters({
      detailsContrat: 'contrat/detailsContrat'
    }),

    titreAffiche () {
      return this.isTresoMoisson ? this.resumeContrat.type.categorieContrat : this.resumeContrat.type.sousTypeContrat
    },

    isContratNonEngage () {
      return this.resumeContrat.type.code === 'CAMPAGNE_NON_ENGAGE'
    },

    isTresoMoisson () {
      return this.resumeContrat.type.sousTypeContrat === 'Tréso Moisson'
    },

    isTresoMoissonAFixer () {
      return this.resumeContrat.type.code === 'TRESO_MOISSON_A_FIXER'
    },

    isPrixFerme () {
      return this.resumeContrat.type.categorieContrat === 'Prix ferme'
    },

    isPrixEnCoursConstruction () {
      const list = ['Prix campagne', 'Contrat filière', 'Prix par période']
      return list.includes(this.detailsContrat.type.categorieContrat)
    },

    isPrixProvisoire () {
      const anneeRecolte = Number(this.detailsContrat.anneeRecolte) + 1
      const formatDateLimite = anneeRecolte + '-07-01'
      const dateLimite = parse(formatDateLimite, 'yyyy-MM-dd', new Date())
      return this.isPrixEnCoursConstruction && isBefore(new Date(), dateLimite)
    },
    isQuantitePayeeAffichee () {
      return this.detailsContrat.quantitePayeEnKg && ['EN_COURS_VALORISATION', 'PAYE'].includes(this.resumeContrat.etatPaiement.code)
    },
    isQuantiteLivreeAffichee () {
      return this.detailsContrat.quantiteLivreeEnKg && !this.isQuantitePayeeAffichee && ['EN_COURS_LIVRAISON', 'LIVRE'].includes(this.resumeContrat.etatLogistique.code)
    },

    atLeastOneReglementsHaveTooltipIcon () {
      return this.detailsContrat.reglements.some(reglement => reglement.quantiteReglementEnKg !== this.detailsContrat.quantitePayeEnKg)
    }
  },

  created () {
    this.isOpenModal = this.isOpenModalDetails
    this.loadDetailsContrat()
  },

  methods: {
    ...mapActions({
      getDetailsContrat: 'contrat/getDetailsContrat'
    }),
    loadDetailsContrat () {
      this.loading.detailsContrat = true
      this.erreur.detailsContrat = false

      return this.getDetailsContrat(this.resumeContrat)
        .catch(() => this.erreur.detailsContrat = true)
        .finally(() => this.loading.detailsContrat = false)
    },

    formatPrixReglement (valeur, precision, unite) {
      let formatted = ''

      if (valeur >= 0) {
        formatted += '+ '
      }
      formatted += round(valeur, precision)
      formatted += ' ' + unite
      return formatted
    },

    getChipColor (valeur) {
      if (valeur >= 0) {
        return 'secondary'
      } else {
        return '#EC6608'
      }
    },

    onClose () {
      this.$emit('close')
    },
  }
}
</script>

<style lang="scss" scoped>
.left-vertical-card {
  border-radius: 0 !important;
  overflow-y: auto;
}

.over {
  max-height: 600px;
  overflow: auto;
}

.bg-titre {
  height: 70px;
  background-color: #f1f2f5;
}

.chip {
  width: 95px;
}

.type-reglement {
  width: 95px;
}

.date-reglement {
  width: 85px;
}

.icon {
  width: 23px;
  height: 20px;
}

.align-end {
  text-align: end;
}
</style>
